/**
 * RecruitingPage — tabbed landing page for the Recruiting/ATS domain.
 *
 * Three tabs:
 *   - Job Postings — list of open roles with a "New Job" modal (HR+).
 *   - Candidates   — list of candidate profiles with a "New Candidate" modal.
 *   - Pipeline     — applications grouped by ApplicationStage into columns.
 *
 * The kanban-style grouping is display-only: we render a column per stage and
 * drop cards into them; no drag-drop, no detail pages. Those can be layered on
 * later against the same API client.
 */
import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Briefcase, User as UserIcon, Mail, Sparkles } from "lucide-react";

import { publishJob, scoreAllApplicantsForJob } from "@/api/recruiting";
import { AIScoreBadge } from "@/components/recruiting/AIScoreBadge";
import { JDGeneratorModal } from "@/components/recruiting/JDGeneratorModal";
import { InternalMatchesModal } from "./InternalMatchesModal";

import { pageTransition, formatDate } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";

import {
  listJobPostings,
  createJobPosting,
  listCandidates,
  createCandidate,
  listApplications,
  type JobPosting,
  type JobPostingStatus,
  type EmploymentCommitment,
  type Candidate,
  type CandidateSource,
  type Application,
  type ApplicationStage,
} from "@/api/recruiting";

import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Tabs } from "@/components/ui/Tabs";
import { Skeleton } from "@/components/ui/Skeleton";

const STAGES: ApplicationStage[] = [
  "APPLIED",
  "SCREENING",
  "INTERVIEW",
  "OFFER",
  "HIRED",
  "REJECTED",
  "WITHDRAWN",
];

const STATUS_VARIANT: Record<
  JobPostingStatus,
  "default" | "success" | "warning" | "info" | "danger"
> = {
  DRAFT: "default",
  OPEN: "success",
  PAUSED: "warning",
  CLOSED: "default",
  FILLED: "info",
};

const STAGE_VARIANT: Record<
  ApplicationStage,
  "default" | "success" | "warning" | "info" | "danger"
> = {
  APPLIED: "info",
  SCREENING: "info",
  INTERVIEW: "warning",
  OFFER: "warning",
  HIRED: "success",
  REJECTED: "danger",
  WITHDRAWN: "default",
};

export function RecruitingPage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const isAdmin = user?.role === "SUPER_ADMIN" || user?.role === "HR_ADMIN";

  const [activeTab, setActiveTab] = useState("jobs");
  const [jobs, setJobs] = useState<JobPosting[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showCandidateModal, setShowCandidateModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, c, a] = await Promise.all([
        listJobPostings(),
        listCandidates(),
        listApplications(),
      ]);
      setJobs(j);
      setCandidates(c);
      setApplications(a);
    } catch {
      addToast("error", "Failed to load recruiting data");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const tabs = [
    { id: "jobs", label: "Job Postings", count: jobs.length },
    { id: "candidates", label: "Candidates", count: candidates.length },
    { id: "pipeline", label: "Pipeline", count: applications.length },
  ];

  const newButtonLabel =
    activeTab === "jobs"
      ? "New Job"
      : activeTab === "candidates"
        ? "New Candidate"
        : null;

  return (
    <motion.div {...pageTransition} className="space-y-6">
      <PageHeader
        title="Recruiting"
        subtitle="Manage open roles, candidate pipeline, and offers."
        actions={
          isAdmin && newButtonLabel ? (
            <Button
              onClick={() =>
                activeTab === "jobs"
                  ? setShowJobModal(true)
                  : setShowCandidateModal(true)
              }
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {newButtonLabel}
            </Button>
          ) : undefined
        }
      />

      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        aria-label="Recruiting sections"
      />

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : activeTab === "jobs" ? (
        <JobsList jobs={jobs} onApplicationsChanged={load} />
      ) : activeTab === "candidates" ? (
        <CandidatesList candidates={candidates} />
      ) : (
        <PipelineBoard
          applications={applications}
          onApplicationUpdated={(next) =>
            setApplications((prev) =>
              prev.map((a) => (a.id === next.id ? next : a)),
            )
          }
        />
      )}

      {showJobModal && (
        <NewJobModal
          onClose={() => setShowJobModal(false)}
          onCreated={() => {
            setShowJobModal(false);
            addToast("success", "Job posting created");
            load();
          }}
        />
      )}
      {showCandidateModal && (
        <NewCandidateModal
          onClose={() => setShowCandidateModal(false)}
          onCreated={() => {
            setShowCandidateModal(false);
            addToast("success", "Candidate added");
            load();
          }}
        />
      )}
    </motion.div>
  );
}

function JobsList({
  jobs,
  onApplicationsChanged,
}: {
  jobs: JobPosting[];
  /** Called after a bulk-score run so the parent can re-fetch
      applications + render fresh ai_score values in the pipeline. */
  onApplicationsChanged?: () => void;
}) {
  const { addToast } = useToast();
  const [scoringJobId, setScoringJobId] = useState<string | null>(null);
  const [publishingJobId, setPublishingJobId] = useState<string | null>(null);
  const [localPublished, setLocalPublished] = useState<Record<string, boolean>>(
    {},
  );
  const [matchesJob, setMatchesJob] = useState<JobPosting | null>(null);

  const isPublished = (job: JobPosting): boolean => {
    if (job.id in localPublished) return localPublished[job.id];
    return Boolean(job.is_public_careers_listed);
  };

  const handleTogglePublish = async (job: JobPosting) => {
    const next = !isPublished(job);
    setPublishingJobId(job.id);
    try {
      await publishJob(job.id, next);
      setLocalPublished((prev) => ({ ...prev, [job.id]: next }));
      addToast(
        "success",
        next
          ? "Live on the careers page."
          : "Removed from the careers page.",
      );
      onApplicationsChanged?.();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not update publish state.";
      addToast("error", detail);
    } finally {
      setPublishingJobId(null);
    }
  };

  const handleScoreAll = async (job: JobPosting) => {
    if (
      !confirm(
        `Run AI scoring on every applicant for "${job.title}"? This uses Claude credits (~$0.001 per candidate).`,
      )
    ) {
      return;
    }
    setScoringJobId(job.id);
    try {
      const r = await scoreAllApplicantsForJob(job.id, { only_unscored: false });
      if (r.total === 0) {
        addToast("info", "No applicants to score yet.");
      } else {
        addToast(
          "success",
          `Scored ${r.scored} of ${r.total} applicant(s)${
            r.failed > 0 ? `, ${r.failed} failed` : ""
          }.`,
        );
        onApplicationsChanged?.();
      }
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not score applicants.";
      addToast("error", detail);
    } finally {
      setScoringJobId(null);
    }
  };

  if (jobs.length === 0) {
    return (
      <Card>
        <p className="text-sm text-text-muted text-center py-12">
          No jobs posted yet.
        </p>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {jobs.map((job) => {
        const busy = scoringJobId === job.id;
        return (
          <Card key={job.id} className="flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-accent" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-text-primary">
                  {job.title}
                </h3>
              </div>
              <Badge variant={STATUS_VARIANT[job.status]}>{job.status}</Badge>
            </div>
            <p className="text-xs text-text-muted">
              {job.location || "Remote"} ·{" "}
              {(job.commitment ?? "FULL_TIME").replace("_", " ")}
            </p>
            {job.open_date && (
              <p className="text-xs text-text-muted">
                Opened {formatDate(job.open_date)}
              </p>
            )}
            <div className="mt-2 flex justify-between items-center gap-2 flex-wrap">
              <label
                className={`inline-flex items-center gap-1.5 text-xs ${
                  job.status === "OPEN"
                    ? "text-text-primary cursor-pointer"
                    : "text-text-muted cursor-not-allowed"
                }`}
                title={
                  job.status === "OPEN"
                    ? "Include this job in your public feed (shown on your careers page embed and on the Lenavio-hosted listing)"
                    : "Mark the job as OPEN before publishing"
                }
              >
                <input
                  type="checkbox"
                  checked={isPublished(job)}
                  disabled={
                    publishingJobId === job.id || job.status !== "OPEN"
                  }
                  onChange={() => handleTogglePublish(job)}
                  className="h-3.5 w-3.5 rounded border-border text-accent focus:ring-accent"
                />
                Publish to careers feed
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMatchesJob(job)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-muted transition-colors"
                  title="See internal employees who could fit this role"
                >
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                  Internal matches
                </button>
                <button
                  type="button"
                  onClick={() => handleScoreAll(job)}
                  disabled={busy}
                  className={`inline-flex items-center gap-1.5 rounded-lg border border-accent bg-accent/5 px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/10 transition-colors ${
                    busy ? "opacity-60 cursor-wait" : ""
                  }`}
                  title="Score every applicant on this job with Claude"
                >
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                  {busy ? "Scoring…" : "Score all candidates"}
                </button>
              </div>
            </div>
          </Card>
        );
      })}

      {matchesJob && (
        <InternalMatchesModal
          job={matchesJob}
          onClose={() => setMatchesJob(null)}
        />
      )}
    </div>
  );
}

function CandidatesList({ candidates }: { candidates: Candidate[] }) {
  if (candidates.length === 0) {
    return (
      <Card>
        <p className="text-sm text-text-muted text-center py-12">
          No candidates in the system yet.
        </p>
      </Card>
    );
  }
  return (
    <Card padding={false}>
      <ul className="divide-y divide-border">
        {candidates.map((c) => (
          <li key={c.id} className="px-4 py-3 flex items-center gap-3">
            <div className="rounded-full bg-accent/10 p-2">
              <UserIcon className="h-4 w-4 text-accent" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">
                {c.first_name} {c.last_name}
              </p>
              <p className="text-xs text-text-muted flex items-center gap-1 truncate">
                <Mail className="h-3 w-3" aria-hidden="true" />
                {c.email}
              </p>
            </div>
            <Badge variant="default">{c.source.replace("_", " ")}</Badge>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function PipelineBoard({
  applications,
  onApplicationUpdated,
}: {
  applications: Application[];
  /** Single-row refresh hook fired by the AI Score badge after
      a one-off scoring call so the parent doesn't have to re-fetch. */
  onApplicationUpdated?: (next: Application) => void;
}) {
  const byStage = STAGES.reduce<Record<ApplicationStage, Application[]>>(
    (acc, s) => {
      acc[s] = [];
      return acc;
    },
    {} as Record<ApplicationStage, Application[]>,
  );
  applications.forEach((a) => byStage[a.stage].push(a));

  // Sort each column by AI score descending — unscored rows fall
  // to the bottom. Inter-column order (stages) stays as defined.
  for (const stage of STAGES) {
    byStage[stage].sort((a, b) => {
      const av = a.ai_score ?? -1;
      const bv = b.ai_score ?? -1;
      return bv - av;
    });
  }

  if (applications.length === 0) {
    return (
      <Card>
        <p className="text-sm text-text-muted text-center py-12">
          No applications yet.
        </p>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
      {STAGES.map((stage) => (
        <div key={stage} className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <Badge variant={STAGE_VARIANT[stage]}>{stage}</Badge>
            <span className="text-xs text-text-muted">
              {byStage[stage].length}
            </span>
          </div>
          <div className="space-y-2">
            {byStage[stage].map((a) => (
              <Card key={a.id} className="p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {a.candidate
                      ? `${a.candidate.first_name} ${a.candidate.last_name}`
                      : "Candidate"}
                  </p>
                  <AIScoreBadge
                    application={a}
                    onScored={onApplicationUpdated}
                    compact
                  />
                </div>
                <p className="text-xs text-text-muted truncate">
                  {a.job_posting?.title || "—"}
                </p>
                <p className="text-[10px] text-text-muted">
                  Applied {formatDate(a.applied_at)}
                </p>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function NewJobModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { addToast } = useToast();
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [commitment, setCommitment] =
    useState<EmploymentCommitment>("FULL_TIME");
  const [status, setStatus] = useState<JobPostingStatus>("DRAFT");
  const [description, setDescription] = useState("");
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await createJobPosting({
        title: title.trim(),
        location: location.trim() || null,
        description: description.trim() || null,
        commitment,
        status,
      });
      onCreated();
    } catch {
      addToast("error", "Could not create job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="New job posting" size="lg">
      <div className="space-y-3">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Senior Product Engineer"
        />
        <Input
          label="Location"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Remote"
        />
        <Select
          label="Employment type"
          value={commitment}
          onChange={(e) =>
            setCommitment(e.target.value as EmploymentCommitment)
          }
          options={[
            { value: "FULL_TIME", label: "Full-time" },
            { value: "PART_TIME", label: "Part-time" },
            { value: "CONTRACT", label: "Contract" },
            { value: "INTERNSHIP", label: "Internship" },
            { value: "TEMPORARY", label: "Temporary" },
          ]}
        />
        {/*
          Description + AI generator. Opening the generator pre-fills
          the modal with the in-progress title so the admin doesn't
          have to retype it. The generator hands back markdown that
          we drop straight into this textarea — admin can still
          edit before clicking Create.
        */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label
              htmlFor="job-description"
              className="block text-sm font-medium text-text-primary"
            >
              Description
            </label>
            <button
              type="button"
              onClick={() => setGeneratorOpen(true)}
              className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
            >
              <Sparkles className="h-3 w-3" aria-hidden />
              Generate with AI
            </button>
          </div>
          <textarea
            id="job-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            placeholder="Paste your existing JD, or click Generate with AI."
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
        </div>

        <Select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as JobPostingStatus)}
          options={[
            { value: "DRAFT", label: "Draft" },
            { value: "OPEN", label: "Open" },
            { value: "PAUSED", label: "Paused" },
            { value: "CLOSED", label: "Closed" },
            { value: "FILLED", label: "Filled" },
          ]}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={saving}
            disabled={!title.trim()}
          >
            Create
          </Button>
        </div>
      </div>

      {/* AI generator — opens stacked over this modal. The
          generated markdown is dropped into ``description``
          so the admin can tweak before saving. */}
      <JDGeneratorModal
        isOpen={generatorOpen}
        onClose={() => setGeneratorOpen(false)}
        initialTitle={title}
        onUseDraft={(md) => setDescription(md)}
      />
    </Modal>
  );
}

function NewCandidateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { addToast } = useToast();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [source, setSource] = useState<CandidateSource>("OTHER");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) return;
    setSaving(true);
    try {
      await createCandidate({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        source,
      });
      onCreated();
    } catch {
      addToast("error", "Could not add candidate");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="New candidate" size="lg">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Select
          label="Source"
          value={source}
          onChange={(e) => setSource(e.target.value as CandidateSource)}
          options={[
            { value: "REFERRAL", label: "Referral" },
            { value: "JOB_BOARD", label: "Job board" },
            { value: "COMPANY_SITE", label: "Company site" },
            { value: "AGENCY", label: "Agency" },
            { value: "LINKEDIN", label: "LinkedIn" },
            { value: "OUTBOUND", label: "Outbound" },
            { value: "OTHER", label: "Other" },
          ]}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={saving}
            disabled={
              !firstName.trim() || !lastName.trim() || !email.trim()
            }
          >
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}
